import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Typography,
  CircularProgress,
  Paper,
  Box,
  Divider,
  Chip,
  Button,
  Stack,
} from "@mui/material";
import axios from "axios";
import CustomerLayout from "../CustomerLayout";

const getStatusColor = (status) => {
  const normalized = (status || "").toString().toUpperCase();

  if (["DELIVERED", "COMPLETED"].includes(normalized)) return "success";
  if (["ACCEPTED", "ASSIGNED", "IN_TRANSIT", "ON_HOLD"].includes(normalized)) return "info";
  if (["CANCELLED", "CANCELED", "FAILED"].includes(normalized)) return "error";
  if (["CREATED", "OPEN", "PLACED", "NEW"].includes(normalized)) return "warning";

  return "default";
};

const TrackPackage = () => {
  const history = useHistory();
  const { trackingNumber } = useParams();

  const [packageInfo, setPackageInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`/api/packages/${trackingNumber}`);
      setPackageInfo(response.data);
    } catch (error) {
      setError(error.message || "Failed to load tracking information.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackingNumber]);

  return (
    <CustomerLayout>
      <Box sx={{ width: "100%" }}>
        <Box
          sx={{
            mb: { xs: 3, md: 4 },
            textAlign: { xs: "left", md: "center" },
            maxWidth: 760,
            mx: "auto",
          }}
        >
          <Typography
            variant="h4"
            fontWeight={800}
            gutterBottom
            sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}
          >
            Track Load
          </Typography>

          <Typography variant="body1" color="text.secondary">
            View current status, estimated arrival, and latest location updates for this load.
          </Typography>
        </Box>

        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, sm: 3, md: 4 },
            borderRadius: 3,
            maxWidth: 760,
            mx: "auto",
            border: "1px solid #e2e8f0",
            boxShadow: { xs: "none", md: "0 10px 24px rgba(15, 23, 42, 0.06)" },
          }}
        >
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          ) : !packageInfo ? (
            <Typography variant="body1">Load not found.</Typography>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 2 }}>
                <Typography variant="h5" fontWeight={800} gutterBottom>
                  Load #{trackingNumber}
                </Typography>

                <Chip
                  label={packageInfo.status || "Unknown Status"}
                  color={getStatusColor(packageInfo.status)}
                  variant="outlined"
                  size="small"
                />
              </Box>

              <Divider sx={{ my: 3 }} />

              <Stack spacing={2}>
                <Typography variant="body1">
                  <strong>Estimated Arrival:</strong>{" "}
                  {packageInfo.estimatedDeliveryDate || "Not available yet"}
                </Typography>

                <Typography variant="body1">
                  <strong>Last Known Location:</strong>{" "}
                  {packageInfo.lastKnownLocation || "Not available yet"}
                </Typography>

                <Typography variant="body1">
                  <strong>Tracking Number:</strong> {trackingNumber}
                </Typography>
              </Stack>

              <Divider sx={{ my: 3 }} />

              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={1.5}
                justifyContent="center"
              >
                <Button
                  variant="contained"
                  color="primary"
                  onClick={fetchData}
                  fullWidth
                  sx={{
                    maxWidth: { sm: 220 },
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  Refresh Tracking
                </Button>

                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => history.push("/customer/support-and-help-center")}
                  fullWidth
                  sx={{
                    maxWidth: { sm: 220 },
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  Contact Support
                </Button>
              </Stack>
            </>
          )}
        </Paper>
      </Box>
    </CustomerLayout>
  );
};

export default TrackPackage;